import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, ViewChild, ElementRef, NgZone } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { CalendarDateFormatter, CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarEventTitleFormatter, CalendarView } from 'angular-calendar';
import { JemsCalendarDateFormatter } from './calendar-date-formatter';
import { JemsCalendarEventTitleFormatter } from './calendar-event-title-formatter';
import { MatDialog } from '@angular/material/dialog';
import { FormSessionDialogComponent } from 'src/app/component/form-session-dialog/form-session-dialog.component';
import { GeneralSessionDialogComponent } from 'src/app/component/general-session-dialog/general-session-dialog.component';
import { FormSubSessionDialogComponent } from 'src/app/component/sub-session-dialog/sub-session-dialog.component';
import { ConfirmationDialogComponent } from 'src/app/component/confirmation-dialog/confirmation-dialog.component';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { AdminService } from 'src/app/service/admin.service';
import { EventsService } from 'src/app/service/events.service';
import { Event } from 'src/app/model/event';
import { NotificationService } from 'src/app/service/notification.service';
import { WeekViewHourSegment } from 'calendar-utils';
import * as moment from 'moment';
import { FileRules } from 'src/app/model/file.rules';
import { File } from 'src/app/model/file';
import { Session } from 'src/app/model/session';
import { Submission } from 'src/app/model/paper';
import { CalendarEventTimesChangedEventType } from 'src/app/enum/calendar.changed.type';
import * as _ from 'lodash';
import * as dayjs from 'dayjs';
import { calendarTimezone } from 'src/app/enum/calendar.timezone';
import { FormGroup, FormBuilder } from '@angular/forms';
import { TimezoneService } from 'src/app/service/timezone.service';
import { SegmentedOption } from 'src/app/component/segmented-control/segmented-control.component';
import { EVENT_ADMIN_ICONS } from 'src/app/admin/event-admin/event-admin-icons';
import { isLightBackground } from 'src/app/lib/color-contrast';
import { TranslateService } from '@ngx-translate/core';
import { DEFAULT_SESSION_COLOR } from 'src/app/lib/session-color';

interface ExtendedCalendarEventSessions extends CalendarEvent {
  event: any;
  title: string;
  subtitle: string;
  url: string;
  room: string;
  submissions: Array<Submission>;
}

/** Evento temporário criado pelo drag: carrega os campos que o angular-calendar usa durante o arraste. */
interface DragToCreateSessionEvent extends ExtendedCalendarEventSessions {
  newStart: Date;
  type: CalendarEventTimesChangedEventType;
}

interface calendarTimeZone {
  id: string;
  value: string;
  offset: number;
}

function floorToNearest(amount: number, precision: number) {
  return Math.floor(amount / precision) * precision;
}

function ceilToNearest(amount: number, precision: number) {
  return Math.ceil(amount / precision) * precision;
}

const HEIGHT_ROW_HOUR: number = 100;

/** Classes aplicadas ao bloco da sessão conforme o contraste com a cor de fundo. */
/** Prefixo da classe que carrega o id da sessão no bloco (ver `onWeekPointerUp`). */
const BLOCK_ID_PREFIX = 'session-block-id-';
/** Deslocamento máximo, em px, para o gesto ainda contar como clique. */
const CLICK_MOVE_TOLERANCE = 4;

const BLOCK_ON_LIGHT = 'session-block--on-light';
const BLOCK_ON_DARK = 'session-block--on-dark';

/**
 * Escolhe a cor do texto do bloco da sessão em função da COR DO BLOCO, não do
 * tema: a cor é escolhida pelo organizador e pode ser clara ou escura em
 * qualquer tema. A regra de luminância mora em `lib/color-contrast`, também
 * usada pelo programa exportável.
 */
function textContrastClass(background: string): string {
  return isLightBackground(background) ? BLOCK_ON_LIGHT : BLOCK_ON_DARK;
}

@Component({
  selector: 'app-calendar-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './calendar-view.component.html',
  styleUrls: ['./calendar-view.component.scss'],
  // Régua de horas em 24h quando o idioma é português. Provido AQUI (e não no
  // AppModule) para valer só nesta página, junto dos `mwl-calendar-*` do
  // template — que resolvem o formatador pelo injetor deste componente.
  providers: [
    { provide: CalendarDateFormatter, useClass: JemsCalendarDateFormatter },
    // Sem tooltip nos blocos de sessão.
    { provide: CalendarEventTitleFormatter, useClass: JemsCalendarEventTitleFormatter }
  ]
})
export class CalendarViewComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('containerWeekView') containerWeekView: ElementRef<HTMLDivElement>;
  @ViewChild('calendarHeader') calendarHeader: ElementRef<HTMLElement>;
  @ViewChild('calendarHeaderSpacer') calendarHeaderSpacer: ElementRef<HTMLElement>;

  /** Ícones do admin — usados no conteúdo do bloco de sessão. */
  readonly icons = EVENT_ADMIN_ICONS;

  private readonly destroy$ = new Subject<void>();
  private headerResizeObserver: ResizeObserver;

  /** Loop de sincronia da geometria do chrome fixo (ver `trackFixedChrome`). */
  private chromeRafId = 0;
  /** Última geometria aplicada — evita reescrever estilo sem mudança. */
  private lastChromeLeft = -1;
  private lastChromeWidth = -1;
  private lastHeaderHeight = -1;
  /** Página rolada? Liga a sombra sob a faixa de dias (classe `is-scrolled`). */
  private lastScrolled = false;

  /**
   * Mantém cabeçalho e faixa de dias presos à VIEWPORT (`position: fixed`),
   * com a geometria horizontal copiada da área de conteúdo — mesma técnica do
   * header da Lista de Submissões (`trackFixedHeader`).
   *
   * Por que `fixed` e não `sticky`: elemento sticky é ancorado no DOCUMENTO, e
   * o rubber-band do overscroll move o documento inteiro nos extremos — daí o
   * cabeçalho "tremer" no começo e no fim da página. `fixed` ancora na
   * viewport, que não se move.
   *
   * Por que um loop por frame e não `ResizeObserver`: abrir/fechar o menu
   * lateral ou alternar Moderno↔Clássico muda a POSIÇÃO do conteúdo sem mudar
   * o tamanho do observado — o observer não dispara, e eventos não cobrem os
   * quadros da animação.
   */
  /**
   * Decide se o bloco fantasma pode aparecer, pela posição do ponteiro.
   *
   * Duas situações pedem que ele suma, e nenhuma é resolvida só por CSS:
   *
   * 1. Logo depois de criar a sessão com um clique: o navegador NÃO reavalia
   *    `:hover` sem movimento do mouse, então o segmento continua "hovered" e
   *    o fantasma tapa o bloco recém-nascido.
   * 2. Com o ponteiro sobre QUALQUER bloco de sessão: o segmento de hora fica
   *    por baixo e segue casando com `:hover` em parte da área — o fantasma,
   *    que ocupa duas células, reaparece por cima do bloco.
   *
   * A classe `ghost-suppressed` no host cobre as duas: é ligada na criação e
   * recalculada a cada movimento do ponteiro.
   */
  private suppressGhostUntilPointerMoves(): void {
    (this.elementRef.nativeElement as HTMLElement).classList.add('ghost-suppressed');
  }

  /** Ponteiro sobre um bloco de sessão? Então nada de fantasma. */
  private readonly onWeekPointerMove = (event: MouseEvent): void => {
    const host = this.elementRef.nativeElement as HTMLElement;
    const target = event.target as HTMLElement;
    const overBlock = !!(target && target.closest && target.closest('.cal-event-container'));
    host.classList.toggle('ghost-suppressed', overBlock);
  };

  private readonly trackFixedChrome = (): void => {
    const header = this.calendarHeader?.nativeElement;
    const spacer = this.calendarHeaderSpacer?.nativeElement;

    if (header && spacer) {
      const rect = this.elementRef.nativeElement.getBoundingClientRect();
      const headerHeight = header.offsetHeight;

      if (rect.left !== this.lastChromeLeft || rect.width !== this.lastChromeWidth) {
        header.style.left = `${rect.left}px`;
        header.style.width = `${rect.width}px`;
        this.lastChromeLeft = rect.left;
        this.lastChromeWidth = rect.width;
      }

      if (headerHeight !== this.lastHeaderHeight) {
        spacer.style.height = `${headerHeight}px`;
        // A faixa de dias se prende logo abaixo do cabeçalho.
        this.elementRef.nativeElement.style.setProperty('--calendar-header-h', `${headerHeight}px`);
        this.lastHeaderHeight = headerHeight;
      }

      this.syncDayHeaders(rect);
    }

    // Sombra sob a faixa de dias só quando há conteúdo passando por baixo —
    // mesma lógica do `.scrolled-down` da tabela da Lista de Submissões.
    //
    // Enquanto um diálogo está aberto, o CDK trava a rolagem da página
    // (`cdk-global-scrollblock` põe `position: fixed` no <html> e move o
    // deslocamento para um `top` negativo). Nesse estado `pageYOffset` vale 0 e
    // a sombra sumia ao abrir o editor, voltando ao fechar. Congelamos o estado
    // enquanto a trava existir: a página não rolou de fato.
    if (!document.documentElement.classList.contains('cdk-global-scrollblock')) {
      const scrolled = window.pageYOffset > 0;
      if (scrolled !== this.lastScrolled) {
        this.elementRef.nativeElement.classList.toggle('is-scrolled', scrolled);
        this.lastScrolled = scrolled;
      }
    }

    this.chromeRafId = requestAnimationFrame(this.trackFixedChrome);
  };

  /**
   * Faixa de dias: também `fixed`, alinhada à grade. Como ela sai do fluxo, a
   * altura precisa ser devolvida como `padding-top` da semana — senão a grade
   * de horários sobe e fica por baixo dela.
   */
  private syncDayHeaders(contentRect: DOMRect): void {
    const container: HTMLElement = this.containerWeekView?.nativeElement;
    if (!container) { return; }

    const dayHeaders: HTMLElement = container.querySelector('.cal-day-headers');
    const weekView: HTMLElement = container.querySelector('.cal-week-view');
    if (!dayHeaders || !weekView) { return; }

    const gridRect = container.getBoundingClientRect();
    dayHeaders.style.left = `${gridRect.left}px`;
    dayHeaders.style.width = `${gridRect.width}px`;
    weekView.style.paddingTop = `${dayHeaders.offsetHeight}px`;
  }

  event: Event;
  submissions: Array<Submission>;
  showAlocated: Boolean = false;
  viewDate: Date = new Date();
  events: ExtendedCalendarEventSessions[] = [];
  view: CalendarView = CalendarView.Week;

  /**
   * Segmented de visualização — mesmo padrão de `admin/event-requests`.
   *
   * A visão de DIA está fora das opções por decisão de produto, mas o suporte a
   * ela permanece intacto: o `mwl-calendar-day-view` segue no template, o
   * formatador de 24h cobre `dayViewHour` e o `CalendarView.Day` continua sendo
   * um valor válido de `view`. Para reativar, basta devolver a linha abaixo.
   */
  readonly viewOptions: SegmentedOption[] = [
    { value: CalendarView.Month, label: 'admin.event.sessions.month' },
    { value: CalendarView.Week, label: 'admin.event.sessions.week' },
    // { value: CalendarView.Day, label: 'admin.event.sessions.day' },
  ];

  activeDayIsOpen = true;
  weekStartsOn: 0 = 0;
  updatingSessions: boolean = true;
  language = localStorage.getItem('language');

  dragToCreateActive = false;

  /** Bloco e posição registrados no mousedown (ver `onWeekPointerDown`). */
  private pointerDownBlock: { id: number, x: number, y: number, onAction: boolean } = null;

  public addSubmissionsMode = false;
  public sessionSubmissionMode;

  showFiller = false;

  public viewType;

  displayedColumns: string[] = ['starts', 'ends', 'track', 'title', 'room', 'chair', 'submissions', 'edit', 'delete'];
  tableDataSource: MatTableDataSource<Session>;

  private isTimeZoneOfBrowser: boolean = false;
  private sessionsWereUpdated: boolean = false;

  public timeZoneForm: FormGroup;
  public listTimeZones: Array<any>;
  private timeZoneSelected: calendarTimeZone;

  /**
   * Ações do bloco. Só o "x" — e ele serve APENAS para a lib desenhar o botão:
   * quem trata o clique é `onWeekPointerDown`/`onWeekPointerUp`, na fase de
   * captura, porque o `click` nativo se perde quando a lib recria o nó do
   * bloco no meio do gesto (o mesmo motivo que impedia abrir o editor).
   *
   * O `onClick` fica vazio de propósito: se ele também excluísse, nas vezes em
   * que o clique nativo funciona apareceriam DUAS confirmações.
   */
  actions: CalendarEventAction[] = [
    {
      label: '<i class="jems3s fa-times"></i>',
      a11yLabel: 'Delete',
      onClick: (): void => { /* ver o comentário acima */ },
    }
  ];

  constructor(
    private adminService: AdminService,
    private eventService: EventsService,
    public notificationService: NotificationService,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    public fb: FormBuilder,
    private timezoneService: TimezoneService,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
    private translate: TranslateService
  ) { }

  ngAfterViewInit(): void {
    // Fase de CAPTURA: a lib de arraste chama `stopPropagation()` no mousedown
    // do bloco ("stop mouse events propagating up the chain", em
    // `angular-draggable-droppable`), então um listener no contêiner — que
    // ouve no bubbling — nunca é chamado. Na captura, o evento passa por nós
    // ANTES de chegar ao bloco.
    document.addEventListener('mousedown', this.onWeekPointerDown, true);
    document.addEventListener('mouseup', this.onWeekPointerUp, true);

    this.syncHeaderHeight();
    // Fora da zona: geometria por frame não deve disparar change detection.
    this.ngZone.runOutsideAngular(() => {
      // Só alterna uma classe (nada de leitura de layout), e FORA da zona —
      // `mousemove` dentro dela dispara detecção de mudanças a cada pixel.
      document.addEventListener('mousemove', this.onWeekPointerMove, true);
      this.chromeRafId = requestAnimationFrame(this.trackFixedChrome);
    });

    // A altura do cabeçalho muda quando ele quebra em mais linhas (janela
    // estreita, nome de fuso longo). Um ResizeObserver cobre esses casos sem
    // depender só do evento de resize da janela.
    if (typeof ResizeObserver !== 'undefined' && this.calendarHeader) {
      this.headerResizeObserver = new ResizeObserver(() => this.syncHeaderHeight());
      this.headerResizeObserver.observe(this.calendarHeader.nativeElement);
    }
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.onWeekPointerDown, true);
    document.removeEventListener('mouseup', this.onWeekPointerUp, true);
    document.removeEventListener('mousemove', this.onWeekPointerMove, true);
    cancelAnimationFrame(this.chromeRafId);
    this.headerResizeObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Publica a altura do cabeçalho fixo em `--calendar-header-h`.
   *
   * A linha de dias é `sticky` logo ABAIXO do cabeçalho, e precisa saber a
   * altura dele para parar no lugar certo. Medida em vez de cravada porque o
   * cabeçalho quebra em duas/três linhas em telas estreitas.
   */
  private syncHeaderHeight(): void {
    const header: HTMLElement = this.calendarHeader?.nativeElement;
    if (!header) {
      return;
    }

    this.elementRef.nativeElement.style.setProperty('--calendar-header-h', `${header.offsetHeight}px`);
  }

  ngOnInit() {
    setTimeout(() => {
      this.addSubmissionsMode = false;
      this.route.params.subscribe(params => {
        this.adminService.getEvent().subscribe(event => {
          this.event = event;
          this.viewDate = this.event.eventInfo.beginAt.toDate();
          this.viewType = params.viewType;
          this.eventService.getSessionsByEvent(event.id).subscribe(sessions => {
            this.event.sessions = sessions;
          } , () => {},
          () => {
            this.initFormTimeZone(this.event);
            this.loadSessionsInEventTimeZone();
            this.tableDataSource = new MatTableDataSource<Session>(this.event.sessions);
            if (this.event.sessions.length > 0) {
              const earlierDate: dayjs.Dayjs = this.getEarlierSession(this.event.sessions, this.event);
              this.scrollViewWeekCalendar(earlierDate);
            }
            this.updatingSessions = false;
          });
        });
      });
    });
  }

  private initFormTimeZone(event: Event): void {
    const eventTimeZone: string = event.eventInfo.timeZone;
    this.listTimeZones = calendarTimezone.toSelectable(eventTimeZone, moment.tz.guess(), {
      event: this.translate.instant('forms.fields.legend.event-time-zone'),
      browser: this.translate.instant('forms.fields.legend.browser-time-zone')
    });

    let timezone: string;
    if (!!eventTimeZone) {
      timezone = 'event';
    } else {
      timezone = 'browser';
    }

    this.timeZoneForm = this.fb.group({
      calendarTimeZone: [timezone]
    });
    this.timeZoneSelected = this.listTimeZones.find(item => item.id === timezone);
  }

  private getEarlierSession(sessions: any, event: Event): dayjs.Dayjs {
    const eventBeginAt: dayjs.Dayjs = dayjs(event.eventInfo.beginAt);
    const calendarBeginAt: dayjs.Dayjs = dayjs(eventBeginAt).startOf('week');
    const calendarEndAt: dayjs.Dayjs = dayjs(eventBeginAt).endOf('week');
    let time: dayjs.Dayjs = null;
    for (const session of sessions) {
      const start = dayjs(this.timezoneService.timeZoneConverter(session.start.toDate(), this.offsetEventTimezone));
      if(start.isBefore(calendarEndAt) && (start.isAfter(calendarBeginAt) || start.isSame(calendarBeginAt)) ) {
        if (!time) {
          time = session.start;
        } else if (session.start.isBefore(time)) {
          time = session.start;
        }
      }
    }
    return time ?? dayjs();
  }

  /**
   * Rola a PÁGINA até a hora da sessão mais cedo da semana. O calendário é
   * renderizado inteiro no fluxo, então quem rola é o `html` — antes era o
   * container, que tinha rolagem própria.
   *
   * O alvo desconta a faixa de dias fixa (`sticky`) e o chrome acima dela,
   * medidos no próprio elemento: assim a primeira hora útil aparece logo
   * abaixo da linha dos dias, e não escondida atrás dela.
   */
  private scrollViewWeekCalendar(date: dayjs.Dayjs) {
    const earliestSessionHour: number = this.timezoneService.timeZoneConverter(date.toDate(), this.offsetEventTimezone).getHours();

    const containerWeekView: HTMLDivElement = this.containerWeekView?.nativeElement;
    if (!containerWeekView) {
      return;
    }

    const dayHeaders: HTMLElement = containerWeekView.querySelector('.cal-day-headers');
    const timeEvents: HTMLElement = containerWeekView.querySelector('.cal-time-events');
    if (!dayHeaders || !timeEvents) {
      return;
    }

    const gridTop: number = timeEvents.getBoundingClientRect().top + window.pageYOffset;
    const stickyTop: number = parseFloat(getComputedStyle(dayHeaders).top) || 0;
    const target: number = gridTop + (earliestSessionHour * HEIGHT_ROW_HOUR) - stickyTop - dayHeaders.offsetHeight;

    window.scrollTo({
      top: Math.max(target, 0),
      left: 0,
      behavior: 'smooth'
    });
  }

  fileUploaded(trackFile: FileRules, submission: Submission): File {
    return submission.files.find(file => file.trackFile.name === trackFile.name);
  }

  private loadSessionsInEventTimeZone(): void {
    const offsetEventTimeZone: number = this.offsetEventTimezone;
    this.loadSessions(this.event, offsetEventTimeZone);
  }

  /**
   * Monta os blocos do calendário a partir das sessões do evento.
   *
   * SEM `cloneDeep`: antes o evento inteiro era clonado em profundidade — com
   * sessões e submissões dentro — só para poder MUTAR cada cópia e devolvê-la
   * como evento do calendário, sem estragar o modelo. O clone era o trabalho
   * caro (aparecia como `[Violation] 'setTimeout' handler took …ms`) e roda de
   * novo a cada troca de fuso.
   *
   * Agora cada bloco é um OBJETO NOVO, montado com os campos que o calendário
   * usa. O `map` já criava um array novo; o modelo continua intocado porque
   * nada dele é escrito. `submissions` é passado por referência de propósito:
   * a lista não é alterada aqui, e copiá-la traria de volta o custo.
   */
  loadSessions(event: Event, utcOffset: number = null) {
    setTimeout(() => {
      this.events = event.sessions.map(iSession => {
        let start: Date = moment(iSession.start.toJSON()).toDate();
        let end: Date = moment(iSession.end.toJSON()).toDate();

        if (utcOffset != null) {
          start = this.timezoneService.timeZoneConverter(start, utcOffset);
          end = this.timezoneService.timeZoneConverter(end, utcOffset);
        }

        return {
          ...(iSession as any),
          start,
          end,
          title: iSession.title,
          subtitle: iSession.subtitle,
          submissions: iSession.submissions,
          draggable: true,
          actions: this.actions,
          resizable: {
            beforeStart: true,
            afterEnd: true,
          },
          color: {
            primary: '#FFFFFF',
            secondary: iSession.backgroundColor,
          },
          cssClass: `${textContrastClass(iSession.backgroundColor)} ${BLOCK_ID_PREFIX}${iSession.id}`,
        } as ExtendedCalendarEventSessions;
      });
      this.refresh();
    });
  }

  private eventUpdateSchedule(start: Date, end: Date, sessionID: number): void {
    this.event.sessions = this.event.sessions.map((session: Session) => {
      if (session.id === sessionID) {
        return {
          ...session,
          start: dayjs(start),
          end: dayjs(end),
        };
      }
      return session;
    });
  }

  public changeViewTimeZone(id: string): void {
    this.timeZoneSelected = this.listTimeZones.find(item => item.id === id);

    this.timeZoneSelected.id === 'browser' ? this.isTimeZoneOfBrowser = true : this.isTimeZoneOfBrowser = false;

    if (this.sessionsWereUpdated) {
      this.loadSessions(this.event, this.timeZoneSelected.offset);
      this.sessionsWereUpdated = false;
    } else {
      this.loadSessions(this.event, this.timeZoneSelected.offset);
    }
  }

  public get offsetEventTimezone(): number {
    const nameTimeZone: string = this.event.eventInfo.timeZone;
    const offsetTimeZone: number = moment.tz(nameTimeZone).utcOffset();

    return this.timeZoneSelected?.offset ?? offsetTimeZone;
  }

  /**
   * Detecção de clique no bloco FEITA POR NÓS, no par mousedown/mouseup.
   *
   * O `(eventClicked)` da lib depende do evento `click` nativo, que o navegador
   * só dispara se o MESMO nó receber mousedown e mouseup. Durante o arraste a
   * lib recalcula a semana inteira a cada `mousemove`
   * (`dragMove` → `restoreOriginalEvents` → `getWeekView`), substituindo o nó
   * do bloco — com um tremor de mouse, o `click` nunca acontece e o editor não
   * abria. Aqui não dependemos do nó sobreviver: guardamos o ID e a posição no
   * mousedown e decidimos no mouseup, por tolerância de deslocamento.
   */
  private readonly onWeekPointerDown = (event: MouseEvent): void => {
    this.pointerDownBlock = null;

    const target = event.target as HTMLElement;
    if (!target) { return; }

    const container = target.closest('.cal-event-container') as HTMLElement;
    if (!container) { return; }

    const idClass = Array.from(container.classList).find(c => c.indexOf(BLOCK_ID_PREFIX) === 0);
    if (!idClass) { return; }

    this.pointerDownBlock = {
      id: Number(idClass.substring(BLOCK_ID_PREFIX.length)),
      x: event.clientX,
      y: event.clientY,
      // O "x" de excluir sofre do MESMO problema do bloco: o `click` nativo se
      // perde quando a lib recria o nó no meio do gesto. Por isso ele também é
      // resolvido aqui, e não pelo `onClick` da action — só mudamos o que fazer
      // no mouseup.
      onAction: !!target.closest('.cal-event-action'),
    };
  };

  private readonly onWeekPointerUp = (event: MouseEvent): void => {
    const down = this.pointerDownBlock;
    this.pointerDownBlock = null;
    if (!down) { return; }

    const moved = Math.abs(event.clientX - down.x) > CLICK_MOVE_TOLERANCE
               || Math.abs(event.clientY - down.y) > CLICK_MOVE_TOLERANCE;
    if (moved) { return; }   // foi arraste, não clique

    const block = this.events.find(iEvent => iEvent.id === down.id);
    if (!block) { return; }

    this.handleSession(down.onAction ? 'delete' : 'clicked', block);
  };

  handleSession(action: string, session: any): void {
    if ( action === 'create/edit' ) {
      const dialogRef = this.dialog.open(GeneralSessionDialogComponent, {
        // Geometria FIXA, dimensionada pela aba "Configurações gerais" (a que
        // se abre): título ~30px + faixa de abas ~36px + painel 305px + barra
        // de ações ~44px + padding do container 30px ≈ 445px. A aba
        // "Submissões" é mais alta e rola por dentro. O `maxHeight` é guarda
        // para telas baixas.
        width: '625px',
        height: '450px',
        maxHeight: '85vh',
        panelClass: ['my-custom-dialog-class', 'session-dialog-panel'],
        disableClose: true,
        // Sem foco inicial num controle: com `autoFocus`, o Material focava o
        // primeiro tabbable (o "x") e o diálogo abria com ele destacado. Em
        // `false` o Material foca o PRÓPRIO container — o foco continua dentro
        // do diálogo (trap e Esc seguem funcionando), sem realce em botão.
        autoFocus: false,
        data: {
          params: {
            session: session,
            eventTracks: this.event.tracks,
            showAlocated: this.showAlocated,
            saveSession: (saveFromDialog) => {
              this.updateSession(saveFromDialog);
            }
          }
        }
      });
      dialogRef.componentInstance.changeAlocated.subscribe(result => {
        this.showAlocated = result as boolean;
      });

      // Clique fora: sem alteração pendente, apenas fecha — é a rota de saída
      // quando "Descartar" está desabilitado (o diálogo usa `disableClose`).
      dialogRef.backdropClick().subscribe(() => {
        if (dialogRef.componentInstance.hasChanges) {
          dialogRef.componentInstance.save();
        } else {
          dialogRef.close();
        }
      });
    } else if (action === 'delete') {
      if (session.id) {
        // Confirmação de exclusão no contrato atual do
        // `ConfirmationDialogComponent`: título curto + conteúdo que EXPLICA a
        // consequência e nomeia a sessão, botão de confirmar rotulado pela ação
        // e `destructive` (confirmar em vermelho). Antes: a pergunta inteira no
        // título, conteúdo vazio (parágrafo em branco), botão genérico
        // "Confirmar" e sem sinalização de ação destrutiva.
        this.dialog.open(ConfirmationDialogComponent, {
          data: {
            title: 'admin.event.sessions.delete-dialog.title',
            content: 'admin.event.sessions.delete-dialog.content',
            params: { content: { title: session.title } },
            // Chave própria: a global `forms.buttons.delete` é "Deletar".
            confirm: 'admin.event.sessions.delete-dialog.confirm',
            destructive: true
          }
        }).afterClosed().subscribe(confirmed => {
          if (confirmed) {
            this.deleteSession(session);
          }
        });
      } else {
          this.events = this.events.filter((iEvent) => iEvent !== session);
      }
    } else if (action === 'clicked') {
      // Clique SIMPLES no bloco abre a edição da sessão. Antes era preciso
      // duplo clique, simulado por um `hasSingleClick` com janela de 250ms.
      // O diálogo abre INDEPENDENTE do resultado da busca: ela só serve para
      // trazer o coordenador atualizado. Antes, `openFormSessionDialog` vivia
      // dentro do `next` — se a requisição falhasse, o clique não produzia nada
      // e nenhum erro aparecia, o que se parece com "às vezes não abre".
      this.eventService.getSessionsByEvent(this.event.id).subscribe({
        next: sessions => {
          const updated = sessions.find(item => item.id === session.id);
          if (updated) {
            session.chair = updated.chair;
          }
        },
        // `error` NÃO é seguido de `complete` no RxJS — por isso os dois
        // caminhos abrem o diálogo explicitamente.
        error: () => this.openFormSessionDialog(session),
        complete: () => this.openFormSessionDialog(session)
      });
    }
  }

  private openFormSessionDialog(session): void {
    const dialogRef = this.dialog.open(GeneralSessionDialogComponent, {
          width: '625px',
          height: '450px',
          maxHeight: '85vh',
          panelClass: ['my-custom-dialog-class', 'session-dialog-panel'],
          disableClose: true,
          autoFocus: false,
          data: {
            params: {
              session: session,
              eventTracks: this.event.tracks,
              showAlocated: this.showAlocated,
              eventTimeZone: this.event.eventInfo.timeZone,
              saveSession: (saveFromDialog) => {
                this.updateSession(saveFromDialog);
              }
            }
          }
        });
        dialogRef.componentInstance.changeAlocated.subscribe(result => {
          this.showAlocated = result as boolean;
        });

        // Clique fora: sem alteração pendente, apenas fecha — é a rota de saída
        // quando "Descartar" está desabilitado (o diálogo usa `disableClose`).
        dialogRef.backdropClick().subscribe(() => {
          if (dialogRef.componentInstance.hasChanges) {
            dialogRef.componentInstance.save();
          } else {
            dialogRef.close();
          }
        });

        dialogRef.afterClosed().subscribe(() => {
          this.loadSessions(this.event, this.timeZoneSelected.offset);
        });
  }

  eventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): void {
    const changedSession = event as ExtendedCalendarEventSessions;

    // Arraste que não moveu nada (o mouse tremeu, mas o deslocamento não chegou
    // a um passo da grade) chega aqui com os MESMOS horários. Ignorar é o que
    // faz o clique simples voltar a funcionar: sem isso, o `map` abaixo recria
    // o array, o Angular substitui o nó do bloco entre o mousedown e o mouseup,
    // e o navegador não completa o `click` — o editor não abria. De quebra,
    // evita um PUT ao servidor a cada tremor.
    const sameStart = new Date(changedSession.start).getTime() === new Date(newStart).getTime();
    const sameEnd = new Date(changedSession.end).getTime() === new Date(newEnd).getTime();
    if (sameStart && sameEnd) {
      return;
    }

    this.events = this.events.map(iEvent => {
      if (iEvent === changedSession) {
        changedSession.start = newStart;
        changedSession.end = newEnd;
        return {
          ...changedSession,
          start: newStart,
          end: newEnd
        };
      }
      return iEvent;
    });
    this.updateSession(changedSession, false);
  }

  updateSession(eventSession, isTimeZoneConverted = true) {
    let tempChair = eventSession.chair;
    eventSession.chair = eventSession.chair?.id ?? null;
    this.updatingSessions = true;

    const cloneEventSession = _.cloneDeep(eventSession);
    if (!this.isTimeZoneOfBrowser && !isTimeZoneConverted) {
      const sessionStart = cloneEventSession.start;
      const sessionEnd = cloneEventSession.end;
      cloneEventSession.start = this.timezoneService.convertTimeToBrowserTimezone(sessionStart, null, this.offsetEventTimezone);
      cloneEventSession.end = this.timezoneService.convertTimeToBrowserTimezone(sessionEnd, null, this.offsetEventTimezone);
    }

    if (eventSession.id) {
      this.eventService.editSession(cloneEventSession, eventSession.id).subscribe(session => {
        this.updateEventSession(session);
        this.events = this.events.map(iEvent => {
          if (iEvent.id === session.id) {
            return {
              ...iEvent,
              title: session.title,
              subtitle: session.subtitle,
              url: session.url,
              room: session.room,
              chair: tempChair,
              submissions: session.submissions,
              backgroundColor: session.backgroundColor,
              color : {
                primary: '#FFFFFF',
                secondary: session.backgroundColor,
              },
              // A cor pode ter mudado no diálogo — reavalia o contraste.
              cssClass: `${textContrastClass(session.backgroundColor)} ${BLOCK_ID_PREFIX}${session.id}`,
            };
          }
          return iEvent;
        });
        this.updatingSessions = false;
        this.refresh();
      });
    } else {
      this.eventService.createSession(cloneEventSession).subscribe(session => {
        this.event.sessions.push(session);
        this.events = this.events.map(iEvent => {
          if (iEvent === eventSession) {
            return {
              ...iEvent,
              id: session.id,
              // A classe com o id é o que permite ABRIR o editor pelo clique:
              // `onWeekPointerDown` lê o id de `session-block-id-N`. O bloco
              // nasce sem ela (o id só existe depois desta resposta), e sem
              // isto só passava a abrir quando a grade era reconstruída — o
              // sintoma era "clico na sessão nova e o modal não abre".
              cssClass: `${textContrastClass(iEvent.color?.secondary || DEFAULT_SESSION_COLOR)} ${BLOCK_ID_PREFIX}${session.id}`,
            };
          }
          return iEvent;
        });
        this.refresh();
        this.updatingSessions = false;
      });
    }
  }

  private updateEventSession(iEvent) {
    if (this.isTimeZoneOfBrowser)
      this.sessionsWereUpdated = true;

    this.event.sessions = this.event.sessions.map((session: Session) => {
      if (session.id === iEvent.id) {
        return {
          ...session,
          title: iEvent.title,
          subtitle: iEvent.subtitle,
          url: iEvent.url,
          room: iEvent.room,
          start: iEvent.start,
          end: iEvent.end,
          submissions: iEvent.submissions,
          backgroundColor: iEvent.backgroundColor,
        };
      }
      return session;
    });
  }

  private deleteSessionFromEvent(sessionID: number): void {
    this.event.sessions = this.event.sessions.filter((session: Session) => session.id !== sessionID);
  }

  deleteSession(session) {
    this.eventService.deleteSession(session.id, this.event).subscribe(() => {
      this.events = this.events.filter((iEvent) => iEvent !== session);
      this.deleteSessionFromEvent(session.id);
      this.refresh();
    });
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  onVizualizationChange(value: CalendarView) {
    this.setView(value);
  }

  dayClicked({ date, events }: { date: Date; events: ExtendedCalendarEventSessions[] }): void {
    if (moment(this.viewDate).isSame(date, 'month')) {
        if (
          (moment(this.viewDate).isSame(date, 'day') && this.activeDayIsOpen === true) ||
          events.length === 0
        ) {
          this.activeDayIsOpen = false;
        } else {
          this.activeDayIsOpen = true;
        }
        this.viewDate = date;
      }
  }

  /**
   * Clique simples numa célula vazia cria a sessão; arrastar durante o clique
   * define a duração. Antes exigia DUPLO clique, simulado por um
   * `hasSingleClick` com janela de 250ms.
   */
  startDragToCreate(
  segment: WeekViewHourSegment,
  mouseDownEvent: MouseEvent,
  segmentElement: HTMLElement
  ) {
    const dragToSelectEvent: DragToCreateSessionEvent = {
      id: null,
      event: this.event.id,
      // Título no idioma da interface: era 'New session' fixo, então aparecia em
      // inglês mesmo com o JEMS3 em português. Mesma chave usada ao criar pela
      // página Listar Programa.
      title: this.translate.instant('admin.event.sessions.new-session-title'),
      subtitle: '',
      newStart: segment.date,
      type: CalendarEventTimesChangedEventType.Drag,
      url: '',
      room: '',
      start: segment.date,
      submissions: [],
      color : {
        primary: '#FFFFFF',
        secondary: DEFAULT_SESSION_COLOR,
      },
      cssClass: textContrastClass(DEFAULT_SESSION_COLOR),
      end: (moment(segment.date).add(30, 'minutes').add(0, 'days')).toDate(), /**Add 30 minutes in a new session by default*/
      meta: {
        tmpEvent: true,
      },
      draggable: true,
      actions: this.actions,
      resizable: {
          beforeStart: true,
          afterEnd: true,
        },
    };
    this.events = [...this.events, dragToSelectEvent];
    this.suppressGhostUntilPointerMoves();
    const segmentPosition = segmentElement.getBoundingClientRect();
    this.dragToCreateActive = true;
    const today = moment();
    const endOfView = today.endOf('week');

    this.updateSession(dragToSelectEvent, false);

    fromEvent(document, 'mousemove')
    .pipe(
      finalize(() => {
        delete dragToSelectEvent.meta.tmpEvent;
        this.dragToCreateActive = false;
        this.refresh();
      }),
      takeUntil(fromEvent(document, 'mouseup'))
    )
    .subscribe((mouseMoveEvent: MouseEvent) => {
      const minutesDiff = ceilToNearest(
        mouseMoveEvent.clientY - segmentPosition.top,
        30
      );

      const daysDiff =
        floorToNearest(
          mouseMoveEvent.clientX - segmentPosition.left,
          segmentPosition.width
        ) / segmentPosition.width;

      const newEnd = moment(segment.date).add(minutesDiff, 'minutes').add(daysDiff, 'days');
      if (newEnd > moment(segment.date) && newEnd < endOfView) {
        dragToSelectEvent.end = newEnd.toDate();
      }
      this.refresh();
    });
  }

  private refresh() {
    this.events = [...this.events];
    this.cdr.detectChanges();
  }
}
